import React from 'react';
import Wrapper from 'src/components/Modules-revamp/BlockWrapper';
import BlockTitle from '@/src/components/Modules-revamp/BlockTitle';
import Type from 'src/components/Modules-revamp/CarouselType';
import Animation from 'src/components/Modules-revamp/Animation';
import ModuleWidth from 'src/components/Modules-revamp/ModuleWidthSelect';
import AutoPlay from 'src/components/Modules-revamp/AutoPlay';
import contentCarouselRenderTemplate from 'src/strategies/render/contentCarousel';
import {
  ResponsiveContainer,
  ResponsiveField,
  formAnimationList,
  formButtonList,
} from '@/src/strategies/util';
import LeaveGapBetweenProducts from '@/src/components/Modules-revamp/LeaveGapBetween';
import CallToActionButtonTheme from '@/src/components/Modules-revamp/CallToActionButtonTheme';
import { get } from 'lodash';
import { ContentModuleGroupPreloadingContext } from '@/src/constrains/DraggableModules/PreloadComponent/SubscriptionPreloading';
import { Field } from '@/src/components/RcForm';
import moduleStyleRender from '../../ModuleStyle';
import { EnumModuleType } from '../../../../pages/SalesChannelContentEditor/common';
import ItemToShow from '@/src/components/Modules-revamp/ItemToShow';
import ContentPages from '@/src/components/Modules-revamp/ContentPages';
import { useContentPageCarousel } from '../../hooks/useContentPageCarousel';
import {
  checkResponsiveContentCarousel,
  validateContentImgList,
} from 'src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import ContentText from '@/src/components/Modules-revamp/ContentText';

type ResponsiveFieldProps = {
  field: ResponsiveField | ResponsiveField[];
  disabled?: boolean;
  options?: Array<{ label: string; value: any }>;
  hasModal?: boolean;
  modalFunc?: any;
};

const contentCarouselRender = (form: any, breakpoint: any, extra: any) => {
  const values = form.getFieldsValue(true);
  const {
    handleOnSectionReset,
    handleOnSectionCopy,
    //options,
    salesChannelMode,
  } = extra;
  const list = contentCarouselRenderTemplate(
    values,
    breakpoint,
    salesChannelMode,
  );
  // const { inUsePages = [] } = options || {};

  const renderComponents = (
    {
      type,
      field,
    }: {
      title: string;
      type?: ResponsiveContainer;
      field: Array<any>;
    },
    breakpoint: any,
  ) => {
    if (type === ResponsiveContainer.ModuleStyle) {
      return field.map((f) => moduleStyleRender(f));
    } else {
      return field.map((f) => contentCarouselComponent(f, breakpoint, extra));
    }
  };
  return (
    <>
      {list.map((item) => {
        const component = renderComponents(item, breakpoint);
        const validator = get(item, 'validator');

        const validatorWithExtraProps = (value, extra) => {
          const res = validator(value, {
            ...(extra || {}),
            // contentPages: inUsePages,
          });

          return res;
        };

        return (
          <Wrapper key={item.title} id={`${item?.id}`} breakpoint={breakpoint}>
            <BlockTitle
              id={item?.id}
              title={item?.title}
              breakpoint={breakpoint}
              onReset={() => handleOnSectionReset(item?.fields, breakpoint)}
              onCopy={() => handleOnSectionCopy(item?.fields, breakpoint)}
              headerErrorDependencies={get(item, 'headerErrorDependencies')}
              validator={validatorWithExtraProps}
            />
            {component}
          </Wrapper>
        );
      })}
    </>
  );
};

const contentCarouselComponent = (
  data: ResponsiveFieldProps,
  breakpoint: any,
  extra: any,
) => {
  const {
    animations,
    hoverAnimations,
    buttons,
    form,
    //options,
    onModalConfirm,
    onAdd,
    onDelete,
    onSort,
    scContentPages,
  } = extra || {};
  const draggableId = get(form.getFieldsValue(true), 'draggableId');
  switch (data.field) {
    case ResponsiveField.CarouselType: {
      return <Type disabled={data?.disabled} options={data?.options || []} />;
    }
    case ResponsiveField.ModuleWidth: {
      return (
        <ModuleWidth disabled={data?.disabled} options={data?.options || []} />
      );
    }
    case ResponsiveField.AutoPlay: {
      return <AutoPlay {...data} />;
    }
    case ResponsiveField.ContentPageToShow: {
      return (
        <ItemToShow
          disabled={data?.disabled}
          options={data?.options || []}
          moduleType={EnumModuleType.ResponsiveContentCarousel}
        />
      );
    }
    case ResponsiveField.LeaveGap: {
      return (
        <LeaveGapBetweenProducts
          moduleType={EnumModuleType.ResponsiveContentCarousel}
          options={data?.options || []}
          disabled={data?.disabled}
        />
      );
    }
    case ResponsiveField.CallToActionButton: {
      return (
        <CallToActionButtonTheme
          options={formButtonList(buttons)}
          disabled={data?.disabled}
        />
      );
    }
    case ResponsiveField.Animation: {
      return (
        <Animation
          disabled={data?.disabled}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
        />
      );
    }
    case ResponsiveField.Contents: {
      const { checkEachContentPageError } = checkResponsiveContentCarousel();
      const {
        checkContentPageExistingError,
        checkContentPageImageError,
        checkContentPageSummaryError,
        checkSubtitleError,
      } = validateContentImgList();
      return (
        <ContentPages
          validators={{
            contentPage: checkContentPageExistingError,
            image: checkContentPageImageError,
            subtitle: checkSubtitleError,
            summary: checkContentPageSummaryError,
          }}
          allValidator={checkEachContentPageError}
          moduleType={EnumModuleType.ResponsiveContentCarousel}
          draggableId={draggableId}
          breakpoint={breakpoint}
          disabled={data?.disabled}
          scContentPages={scContentPages}
          hasModal={data?.hasModal}
          onModalConfirm={onModalConfirm}
          onAdd={onAdd}
          onDelete={onDelete}
          onSort={onSort}
        />
      );
    }
    case ResponsiveField.ContentText: {
      return (
        <ContentText
          moduleType={EnumModuleType.ResponsiveProductCarousel}
          disabled={data?.disabled}
        />
      );
    }
  }
};

export default ({ form, breakpoint, ...extra }: any) => {
  const { handleOnModalConfirm, handleOnAdd, handleOnDelete, handleOnSort } =
    useContentPageCarousel({ form });
  const {
    animations,
    hoverAnimations,
    buttons, // options,
    scContentPages,
  } = React.useContext(ContentModuleGroupPreloadingContext);
  return (
    <Field shouldUpdate>
      {() =>
        contentCarouselRender(form, breakpoint, {
          onModalConfirm: handleOnModalConfirm,
          onAdd: handleOnAdd,
          onDelete: handleOnDelete,
          onSort: handleOnSort,
          animations,
          buttons,
          hoverAnimations,
          // options,
          form,
          scContentPages,
          ...extra,
        })
      }
    </Field>
  );
};
